import { Delete, Edit } from "@mui/icons-material";
import { Box, Chip, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { MetodoPagamento } from "../../../../core/enum/MetodoPagamento";
import { useDataHora } from "../../../../core/hooks/useDataHora";
import { useMoeda } from "../../../../core/hooks/useMoeda";
import { IPagamentoPedido } from "../../../interface/IPagamentoPedido";

interface CartaoPagamentoProps {
    pagamento: IPagamentoPedido;
    onEditar: () => void;
    onRemover: () => void;
    onAlternarPago: (pago: boolean) => void;
}

/**
 * Um pagamento por cartão, no lugar de uma linha de tabela.
 *
 * A tabela chegou a doze colunas depois dos campos de conciliação, e no drawer isso virava
 * rolagem horizontal. Em cartão, o que sempre existe — valor, método e situação — fica em
 * destaque, e os campos de cartão aparecem embaixo apenas quando preenchidos.
 */
export const CartaoPagamento = ({ pagamento, onEditar, onRemover, onAlternarPago }: CartaoPagamentoProps) => {
    const { real } = useMoeda();
    const { dataHoraBr } = useDataHora();

    const ehDinheiro = pagamento.metodoPagamento === MetodoPagamento.Dinheiro;
    const veioDaIntegracao = pagamento.origemDadosPagamento === "integracao";

    // Só os campos preenchidos viram chip: pagamento em dinheiro não tem bandeira, e um
    // cartão sem autorização informada não deve exibir traço nenhum.
    const dadosConciliacao = [
        pagamento.maquininhaNome,
        pagamento.tipoCartao,
        pagamento.bandeiraNome,
        pagamento.numeroAutorizacao && `Aut. ${pagamento.numeroAutorizacao}`,
    ].filter(Boolean) as string[];

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                padding: 1.75,
                borderRadius: "12px",
                border: theme => `1px solid ${theme.palette.divider}`,
                // Faixa lateral verde quando confirmado: dá para varrer a lista e ver o que
                // já foi recebido sem ler cada rótulo.
                borderLeft: theme => `3px solid ${pagamento.pago ? theme.palette.success.main : theme.palette.divider}`,
                backgroundColor: theme =>
                    pagamento.pago ? alpha(theme.palette.success.main, 0.04) : "transparent",
                transition: theme => theme.transitions.create(["border-color", "background-color"], { duration: 150 }),
                "&:hover": {
                    borderColor: theme => alpha(theme.palette.primary.main, 0.4),
                    borderLeftColor: theme => pagamento.pago ? theme.palette.success.main : alpha(theme.palette.primary.main, 0.4),
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, lineHeight: 1.2 }}>
                        {real(pagamento.valor)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                        {pagamento.metodoPagamento ?? "Método não informado"}
                        {pagamento.dataHora && ` · ${dataHoraBr(pagamento.dataHora as string)}`}
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
                    <Tooltip title="Editar pagamento">
                        <IconButton size="small" onClick={onEditar}>
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Remover pagamento">
                        <IconButton size="small" color="error" onClick={onRemover}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {ehDinheiro && !!pagamento.valorRecebido && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                    Recebido {real(pagamento.valorRecebido)} · troco {real(pagamento.valorTroco ?? 0)}
                </Typography>
            )}

            {!!dadosConciliacao.length && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {dadosConciliacao.map(dado => (
                        <Chip key={dado} size="small" variant="outlined" label={dado} sx={{ height: 22, fontSize: "0.7rem" }} />
                    ))}
                    {/* Distingue o dado que veio da operadora daquele digitado no balcão. */}
                    {veioDaIntegracao && (
                        <Tooltip title="Dados preenchidos pela integração de pagamento">
                            <Chip size="small" color="info" label="Automático" sx={{ height: 22, fontSize: "0.7rem" }} />
                        </Tooltip>
                    )}
                </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                <Switch
                    checked={!!pagamento.pago}
                    onChange={event => onAlternarPago(event.target.checked)}
                    color="success"
                    size="small"
                />
                <Typography
                    variant="body2"
                    sx={{ fontSize: "0.8125rem", fontWeight: 600 }}
                    color={pagamento.pago ? "success.main" : "text.secondary"}
                >
                    {pagamento.pago ? "Pago" : "Pendente"}
                </Typography>
            </Box>
        </Box>
    );
};
